/* eslint-disable max-len */

import moment from 'moment';
import { useMemo } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import { getClosestDate, getDefaultPlantingDates, getZoneByDate } from '/@/core/dates';
import settings from '/@/core/settings';
import { useHasWriteAccess, useIsValidatedBayerUserType, useIsValidatedSquad } from '/@/hooks/session';
import {
    createRecommendationCommentFromAPI,
    createRecommendationFromAPI,
    deleteRecommendationCommentFromAPI,
    getAllSoyPlantingRecommendationFromAPI,
    getCornDensityCurveForProductsFromAPI,
    getEnvironmentsFromAPI,
    getFeaturesForProductsFromAPI,
    getFertilizationCoefficientsForProductsFromAPI,
    type getFertilizationCoefficientsForProductsFromAPIProps,
    getMetricsForProductsFromAPI,
    getMetricsReferencesFromAPI,
    getPestControlPlansFromAPI,
    getPestsFromAPI,
    getPhenologyForProductsFromAPI,
    getProductsFromAPI,
    getRecommendationCommentByIdFromAPI,
    getRecommendationCommentsFromAPI,
    getSoilMoisturesFromAPI,
    getSoilTypesAndIrrigationChoicesFromAPI,
    getYieldByEnvironmentForProductsFromAPI,
    markRecommendationCommentsAsReadFromAPI,
    type RecommendationComment,
    type RecommendationCommentListParams,
    shareRecommendationFromAPI,
    unshareRecommendationFromAPI,
    updateRecommendationCommentFromAPI,
    updateRecommendationFromAPI,
} from '/@/store/private-data/recommendations/apiProviders.ts';
import { getValidPlantingDates } from '/@/store/private-data/recommendations/selectors';
import { localeSort } from '/@/utils';
import { useOwnUserNode } from '../nodes';
import { useWeedControlPlansFromAPI, useWeedsFromAPI } from '../weeds';
import { getWeedControlPlansFromAPI } from '../weeds/apiProviders';

export const useSoilMoistures = (
    { lat, lng, countryCode, climatePattern, plantingDate = undefined },
    options: { enabled?: boolean } = { enabled: true }
) => {
    const { enabled } = options;

    let key = null;
    if (enabled && !!countryCode && !!climatePattern) {
        key = { lat, lng, countryCode, climatePattern, plantingDate, key: 'useSoilMoistures' };
    }
    return useSWRImmutable(key, getSoilMoisturesFromAPI);
};

export const useSoilTypesAndIrrigationChoices = (
    { lat, lng, countryCode, climatePattern, soilMoisture, plantingDate = undefined },
    options: { enabled?: boolean } = { enabled: true }
) => {
    const { enabled } = options;

    let key = null;
    if (
        enabled &&
        !!lat &&
        !!lng &&
        !!countryCode &&
        !!climatePattern &&
        (soilMoisture !== null || soilMoisture !== undefined)
    ) {
        key = { lat, lng, countryCode, climatePattern, soilMoisture, plantingDate, key: 'useSoilTypes' };
    }
    return useSWRImmutable(key, getSoilTypesAndIrrigationChoicesFromAPI);
};

export const useEnvironments = (
    {
        lat,
        lng,
        countryCode,
        plantingDate,
        climatePattern,
        soilMoisture,
        crop,
        soilType,
        irrigation,
    }: {
        lat: number;
        lng: number;
        countryCode?: string;
        plantingDate?: string;
        climatePattern?: number;
        soilMoisture?: number;
        crop?: 'corn' | 'soy';
        soilType?: number;
        irrigation?: number;
    },
    { enabled = true, onSuccess = undefined }: { enabled?: boolean; onSuccess?: (data: any) => void } = {
        enabled: true,
        onSuccess: undefined,
    }
) => {
    let key: {
        lat: number;
        lng: number;
        countryCode: string;
        plantingDate: string;
        climatePattern: number;
        soilMoisture: number;
        crop: 'corn' | 'soy';
        soilType?: number;
        irrigation?: number;
        key: 'useEnvironments';
    } | null = {
        lat,
        lng,
        countryCode,
        plantingDate,
        climatePattern,
        soilMoisture,
        crop,
        soilType,
        irrigation,
        key: 'useEnvironments',
    };

    if (!enabled || !lat || !lng || !countryCode || !plantingDate || !crop) {
        key = null;
    }
    return useSWRImmutable(key, getEnvironmentsFromAPI, { onSuccess });
};

export const useProducts = (
    {
        lat,
        lng,
        plantingDate,
        brandCodename,
        crop,
        environment,
    }: {
        lat: string;
        lng: string;
        plantingDate: string;
        brandCodename: string;
        crop: string;
        environment: string;
    },
    options: { enabled?: boolean } = { enabled: true }
) => {
    const { enabled } = options;

    let key: {
        lat: string;
        lng: string;
        plantingDate: string;
        brandCodename: string;
        crop: string;
        environment: string;
        key: 'useProducts';
    } | null = { lat, lng, plantingDate, brandCodename, crop, environment, key: 'useProducts' };
    if (!enabled || !environment || !lat || !lng || !plantingDate || !crop || !brandCodename) {
        key = null;
    }
    return useSWRImmutable(key, getProductsFromAPI);
};

// ------
export const useMetricsReferences = ({}) =>
    useSWRImmutable({ key: 'useMetricsReferences' }, getMetricsReferencesFromAPI);
export const useMetricsForProducts = ({ crop, productsId }) => {
    const { cache } = useSWRConfig();
    return useSWRImmutable({ crop, productsId, key: 'useMetricsForProducts' }, ({ crop, productsId }) =>
        getMetricsForProductsFromAPI({ crop, productsId }, cache)
    );
};

export const useFeaturesForProducts = ({ crop, productsId }) => {
    const { cache } = useSWRConfig();
    return useSWRImmutable({ crop, productsId, key: 'useFeaturesForProducts' }, ({ crop, productsId }) =>
        getFeaturesForProductsFromAPI({ crop, productsId }, cache)
    );
};

export const useCornDensityCurveForProducts = ({ productsId, environment, minValue, maxValue, step, crop }) => {
    const { cache } = useSWRConfig();
    let key = { productsId, environment, minValue, maxValue, step, key: 'useCornDensityCurve' };
    if (!environment) key = null;
    if (!productsId) key = null;
    if (crop !== 'corn') {
        key = null;
    }
    return useSWRImmutable(key, ({ productsId, environment, minValue, maxValue, step }) =>
        getCornDensityCurveForProductsFromAPI({ productsId, environment, minValue, maxValue, step }, cache)
    );
};

export const useAllSoyPlantingRecommendation = ({ productsId, plantingDate, environment, crop }) => {
    const { cache } = useSWRConfig();
    let key = { productsId, plantingDate, environment, key: 'useAllSoyPlantingRecommendation' };
    if (!productsId || !plantingDate || !environment) key = null;
    if (crop !== 'soy') key = null;

    return useSWRImmutable(key, ({ productsId, plantingDate, environment }) =>
        getAllSoyPlantingRecommendationFromAPI({ productsId, plantingDate, environment }, cache)
    );
};

export const useFertilizationCoefficientsForProducts = ({
    environment,
    products,
}: getFertilizationCoefficientsForProductsFromAPIProps) => {
    const { cache } = useSWRConfig();
    const filteredProducts = products.filter((p) => p.estimatedYield && p.id);
    let key = { environment, products: filteredProducts, key: 'useAllFertilizationCoefficientsRecommendation' };
    if (!environment) key = null;

    return useSWRImmutable(key, ({ environment, products }) =>
        getFertilizationCoefficientsForProductsFromAPI({ environment, products }, cache)
    );
};

export const useYieldByEnvironmentForProducts = ({ productsId, harvestDensity, environment }) => {
    const { cache } = useSWRConfig();
    let key = { productsId, harvestDensity, environment, key: 'useYieldByEnvironmentForProducts' };
    if (!productsId || !harvestDensity || !environment) key = null;
    return useSWRImmutable(key, ({ productsId, harvestDensity, environment }) =>
        getYieldByEnvironmentForProductsFromAPI({ productsId, harvestDensity, environment }, cache)
    );
};

export const usePhenologyForProducts = (
    { crop, lat, lng, productsId, plantingDate },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const { cache } = useSWRConfig();
    let key = { lat, lng, productsId, plantingDate, key: 'usePhenologyForProducts' };
    if (!lat || !lng || !productsId || !plantingDate || crop !== 'corn') key = null;

    return useSWRImmutable(enabled ? key : null, ({ lat, lng, productsId, plantingDate }) =>
        getPhenologyForProductsFromAPI({ lat, lng, productsId, plantingDate }, cache)
    );
};

export const usePestControlPlans = ({
    crop,
    country,
    page,
    pageSize = 1000,
}: {
    crop: string;
    country: string;
    page?: number;
    pageSize?: number;
}) =>
    useSWR({ crop, country, page, pageSize, key: 'usePestControlPlans' }, getPestControlPlansFromAPI, {
        revalidateOnFocus: false,
    });

export const usePests = ({ page, pageSize = 1000 }: { page?: number; pageSize?: number }) =>
    useSWR({ page, pageSize, key: 'usePests' }, getPestsFromAPI, {
        revalidateOnFocus: false,
    });

// Climate Patterns
export const getClimatePatternChoices = () => settings.choices.climatePatterns;

// Weeds
export const getFilteredWeedControlPlans = ({ cropConfig, field, weedControlPlans }) => {
    // HACK: Limit weed recommendation only to BR for v3 release.
    if (field.country.code !== 'BR') return [];
    const weedZone = field.node.zones.find((zone) => zone.type === settings.enums.zoneTypes.weeds.value);

    if (!weedZone) return [];

    const cropZone = getZoneByDate(field.node.zones, cropConfig.crop, cropConfig.plantingDate);
    if (!cropZone) return [];

    return weedControlPlans?.filter(
        (controlPlan) => controlPlan.zone === weedZone.id && controlPlan.cropZones.includes(cropZone.id)
    );
};

const getWeedChoices = (controlPlans = [], allWeeds = []) => {
    const weedIds = new Set();
    controlPlans.forEach((controlPlan) => controlPlan.weeds.forEach((weedId) => weedIds.add(weedId)));
    return allWeeds
        ?.filter((weed) => weedIds.has(weed.id))
        .sort((a, b) => a.order - b.order || localeSort('name')(a, b));
};

export const useSimplifiedDefaultCropConfig = ({ crop = 'corn', zones = [], countryCode }) => {
    const cropConfig = useMemo(() => {
        if (!zones.length) return {};
        const config = { crop };
        const validPlantingDates = zones
            .filter((zone) => zone.type === settings.enums.zoneTypes?.[crop]?.value)
            .map((zone) => [zone.minDate, zone.maxDate]);

        const defaultPlantingDates = getDefaultPlantingDates(zones, config.crop);

        const plantingDate = defaultPlantingDates[0] || getClosestDate(moment(), validPlantingDates);
        config.plantingDate = moment(plantingDate).format('YYYY-MM-DD');
        config.defaultPlantingDate = moment(plantingDate).format('YYYY-MM-DD');

        if (config.crop === 'corn') {
            const climatePatternChoices = getClimatePatternChoices();
            config.climatePattern = climatePatternChoices?.[0]?.value; // Neutral
            config.soilType = null;
            config.irrigation = null;
            if (countryCode === 'MX') {
                config.soilMoisture = 0;
            }
        }
        return { products: [], recommendedEnvironment: {}, ...config };
    }, [crop, zones, countryCode]);

    return cropConfig;
};

export const useDefaultCropConfig = ({ crop, field }) => {
    const { data: allWeeds = [] } = useWeedsFromAPI({ countryCode: field?.country?.code });
    const { data: weedControlPlans } = useWeedControlPlansFromAPI({ page: 1, pageSize: 1000 });

    const cropConfig = useMemo(() => {
        if (!field) return;
        const config = { crop };
        const validPlantingDates = getValidPlantingDates(config, field);
        const defaultPlantingDates = getDefaultPlantingDates(field?.node?.zones || [], config.crop);

        const plantingDate = defaultPlantingDates[0] || getClosestDate(moment(), validPlantingDates);
        config.plantingDate = moment(plantingDate).format('YYYY-MM-DD');
        config.defaultPlantingDate = moment(plantingDate).format('YYYY-MM-DD');

        if (config.crop === 'corn') {
            const climatePatternChoices = getClimatePatternChoices();
            config.climatePattern = climatePatternChoices?.[0]?.value; // Neutral
            config.soilType = null;
            config.irrigation = null;
            if (field?.country?.code === 'MX') {
                config.soilMoisture = 0;
            }
        }

        config.userBetterEnvironment = 15;
        config.userWorseEnvironment = 15;
        config.userPlanterEfficiency = 90;

        const controlPlans = getFilteredWeedControlPlans({
            cropConfig: config,
            field,
            weedControlPlans: weedControlPlans?.results,
        });
        const weeds = getWeedChoices(controlPlans, allWeeds);
        if (weeds?.length > 0) config.weed = weeds[0].id;

        config.pestControlPlans = [];
        return { products: [], recommendedEnvironment: {}, ...config };
    }, [field, crop, weedControlPlans?.results, allWeeds]);

    return cropConfig;
};

// WARNING NOT HOOKS PER SE,
// THESE ARE CREATED ONLY TO BE ABLE TO USE THEM ON ui/src/views/fields/field-list/index.tsx
// SINCE IT'S A CLASS COMPONENT AND HOOKS ARE NOT USABLE. CHECK ON COMPONENT REFACTOR

// Weeds
export const fetchFilteredWeedControlPlans = async ({ cropConfig, field }) => {
    // HACK: Limit weed recommendation only to BR for v3 release.
    if (field.country.code !== 'BR') return [];
    const weedZone = field.node.zones.find((zone) => zone.type === settings.enums.zoneTypes.weeds.value);

    if (!weedZone) return [];

    const cropZone = getZoneByDate(field.node.zones, cropConfig.crop, cropConfig.plantingDate);
    if (!cropZone) return [];
    const weedControlPlans = await getWeedControlPlansFromAPI({ page: 1, pageSize: 1000 });
    return weedControlPlans?.results?.filter(
        (controlPlan) => controlPlan.zone === weedZone.id && controlPlan.cropZones.includes(cropZone.id)
    );
};

export const fetchDefaultCropConfig = async ({ crop, field }) => {
    if (!field) return;
    const allWeeds = await getWeedControlPlansFromAPI({ page: 1, pageSize: 1000 });

    const config = { crop };
    const validPlantingDates = getValidPlantingDates(config, field);
    const defaultPlantingDates = getDefaultPlantingDates(field.node.zones, config.crop);

    const plantingDate = defaultPlantingDates[0] || getClosestDate(moment(), validPlantingDates);
    config.plantingDate = moment(plantingDate).format('YYYY-MM-DD');
    config.defaultPlantingDate = moment(plantingDate).format('YYYY-MM-DD');

    if (config.crop === 'corn') {
        const climatePatternChoices = getClimatePatternChoices();
        config.climatePattern = climatePatternChoices?.[0]?.value; // Neutral
        config.soilType = null;
        config.irrigation = null;
        if (field.country.code === 'MX') {
            config.soilMoisture = 0;
        }
    }

    config.userBetterEnvironment = 15;
    config.userWorseEnvironment = 15;
    config.userPlanterEfficiency = 90;

    const controlPlans = await fetchFilteredWeedControlPlans({ cropConfig: config, field });
    const weeds = getWeedChoices(controlPlans, allWeeds.results);
    if (weeds?.length > 0) config.weed = weeds[0].id;

    config.pestControlPlans = [];
    return { products: [], recommendedEnvironment: {}, ...config };
};

export const useCreateRecommendationMutation = () => {
    return useSWRMutation(
        { key: 'useMutateRecommendation' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    status: 'saved' | 'draft' | 'read_only';
                    backup: any;
                    weed_v2: any;
                    disease: any;
                    plague: any;
                    weeds: any[];
                    crops: any[];
                    field: number;
                    appInstance: number;
                    corn_density?: {
                        plantingDate: string;
                        product_profile: any;
                        climate_pattern: any;
                        soil_moisture: any;
                        irrigation: any;
                        soilType: any;
                        environment: any;
                        recommended_environment: any;
                        user_density: any;
                        user_nitrogen: any;
                        user_planter_efficiency: any;
                    };
                };
            }
        ) => createRecommendationFromAPI(arg)
    );
};

export const useUpdateRecommendationMutation = () => {
    return useSWRMutation(
        { key: 'useUpdateRecommendation' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    id: number;
                    status: 'saved' | 'draft' | 'only_saved';
                    backup: any;
                    weed_v2: any;
                    disease: any;
                    plague: any;
                    weeds: any[];
                    crops: any[];
                    field: number;
                    appInstance: number;
                    corn_density?: {
                        plantingDate: string;
                        product_profile: any;
                        climate_pattern: any;
                        soil_moisture: any;
                        irrigation: any;
                        soilType: any;
                        environment: any;
                        recommended_environment: any;
                        user_density: any;
                        user_nitrogen: any;
                        user_planter_efficiency: any;
                    };
                };
            }
        ) => updateRecommendationFromAPI(arg)
    );
};

// Recommendation Comments Hooks
export const useRecommendationComments = (
    params: RecommendationCommentListParams = {},
    options: { enabled?: boolean } = { enabled: true }
) => {
    const key = options.enabled
        ? {
              key: 'recommendationComments',
              ...params,
          }
        : null;

    return useSWR(key, getRecommendationCommentsFromAPI, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

export const useRecommendationCommentById = (
    id: number | undefined,
    options: { enabled?: boolean } = { enabled: true }
) => {
    const key = options.enabled && id ? { key: 'recommendationCommentById', id } : null;

    return useSWR<RecommendationComment>(key, getRecommendationCommentByIdFromAPI, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        keepPreviousData: true,
    });
};

export const useCreateRecommendationCommentMutation = () => {
    return useSWRMutation(
        { key: 'useCreateRecommendationComment' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    recommendation: number;
                    message: string;
                };
            }
        ) => createRecommendationCommentFromAPI(arg)
    );
};

export const useUpdateRecommendationCommentMutation = () => {
    return useSWRMutation(
        { key: 'useUpdateRecommendationComment' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    id: number;
                    message: string;
                };
            }
        ) => updateRecommendationCommentFromAPI(arg)
    );
};

export const useDeleteRecommendationCommentMutation = () => {
    return useSWRMutation(
        { key: 'useDeleteRecommendationComment' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    id: number;
                };
            }
        ) => deleteRecommendationCommentFromAPI(arg)
    );
};

export const useMarkRecommendationCommentsAsReadMutation = () => {
    return useSWRMutation(
        { key: 'useMarkRecommendationCommentsAsRead' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    recommendation: number;
                };
            }
        ) => markRecommendationCommentsAsReadFromAPI(arg)
    );
};

export const useShareRecommendationMutation = () => {
    return useSWRMutation(
        { key: 'useShareRecommendation' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    recommendationId: number;
                    sharedWith: number;
                };
            }
        ) => shareRecommendationFromAPI(arg)
    );
};

export const useUnshareRecommendationMutation = () => {
    return useSWRMutation(
        { key: 'useUnshareRecommendation' },
        (
            key,
            {
                arg,
            }: {
                arg: {
                    recommendationId: number;
                    sharedWithId: number;
                };
            }
        ) => unshareRecommendationFromAPI(arg)
    );
};

export const useRecommendationCommentsPermissions = (
    commentsStatus?: 'no_comments' | 'has_unread_comments' | 'has_comments',
    userNode?: number
) => {
    const isValidatedSquad = useIsValidatedSquad();
    const { data: ownUserNode } = useOwnUserNode();
    const isOwnUserNode = ownUserNode?.id === userNode;
    const canReadComments = useIsValidatedBayerUserType();
    const hasWriteAccess = useHasWriteAccess();
    const canSendMessages = canReadComments && hasWriteAccess;
    const shouldShowCommentsButton =
        !isOwnUserNode && (canSendMessages || (isValidatedSquad && commentsStatus !== 'no_comments'));

    return useMemo(
        () => ({
            canReadComments,
            canSendMessages,
            shouldShowCommentsButton,
        }),
        [canReadComments, canSendMessages, shouldShowCommentsButton]
    );
};
